import { useRef, useEffect } from "react";
import { useMedia } from "react-use";
import { Timeline } from "vis-timeline";
import { useAppSelector } from "@/app/appStore";

export const useVisibleCursor = ({
  timelineRef,
}: {
  timelineRef: React.RefObject<Timeline>;
}) => {
  const currentTime = useAppSelector((state) => state.player.currentTime);
  const fromTime = useAppSelector((state) => state.player.videoStream.fromTime);
  const toTime = useAppSelector((state) => state.player.videoStream.toTime);
  const isLive = useAppSelector((state) => state.player.videoStream.isLive);

  const state = useRef({
    isFocusAnimating: false,
    isDragging: false,
  });

  useEffect(() => {
    const timeline = timelineRef.current;

    const onRangeChange = () => {
      state.current.isDragging = true;
    };

    const onRangeChanged = () => {
      state.current.isDragging = false;
    };

    timeline?.on("rangechange", onRangeChange);
    timeline?.on("rangechanged", onRangeChanged);

    return () => {
      timeline?.off("rangechange", onRangeChange);
      timeline?.off("rangechanged", onRangeChanged);
    };
  }, [timelineRef]);

  /**
   * If current window duration is bigger than new zoom max, update window range
   */

  useEffect(() => {
    if (!timelineRef.current) {
      return;
    }

    const { start, end } = timelineRef.current.getWindow();
    const currentWindowDuration = end.getTime() - start.getTime();
    const newWindowDuration =
      new Date(toTime).getTime() - new Date(fromTime).getTime();

    const shouldZoomChange =
      fromTime &&
      toTime &&
      !isLive &&
      currentWindowDuration > newWindowDuration;

    const shift = newWindowDuration / 20;

    if (shouldZoomChange) {
      state.current.isFocusAnimating = true;

      timelineRef.current.setWindow(
        new Date(fromTime).getTime() - shift,
        new Date(toTime).getTime() - shift,
        { animation: false },
        () => {
          state.current.isFocusAnimating = false;
        }
      );
    }
  }, [fromTime, toTime, isLive, timelineRef]);

  const isMobile = useMedia("(max-width: 768px)");

  // Keep cursor visible by adjusting visible window
  useEffect(() => {
    if (!timelineRef.current || !currentTime) {
      return;
    }

    const { start, end } = timelineRef.current.getWindow();
    const windowDuration = end.getTime() - start.getTime();

    /**
     * Shift is the amount of time to shift the window to the left
     * If we don't shift the window, the cursor will be at the edge of the window when visible window is moved
     */
    let shift = windowDuration / 20;

    if (isMobile) {
      /**
       * On mobile, we should always keep the cursor at the center of the window
       */
      shift = windowDuration / 2;
      timelineRef.current.setWindow(
        new Date(currentTime - shift),
        new Date(currentTime + windowDuration - shift),
        { animation: false },
        () => {
          state.current.isFocusAnimating = false;
        }
      );
      return;
    }

    const isAnimationIdle =
      !state.current.isFocusAnimating || !state.current.isDragging;

    if (
      (currentTime >= end.getTime() || currentTime <= start.getTime()) &&
      isAnimationIdle
    ) {
      const shouldAnimate = windowDuration < 10000 ? false : true;

      if (shouldAnimate) {
        state.current.isFocusAnimating = true;
      }

      timelineRef.current.setWindow(
        new Date(currentTime - shift),
        new Date(currentTime + windowDuration - shift),
        { animation: shouldAnimate },
        () => {
          state.current.isFocusAnimating = false;
        }
      );
    }
  }, [currentTime, timelineRef]);
};
